import { collection, doc, runTransaction, query, where, getDocs } from 'firebase/firestore';
import { addDoc } from '../../api/FirestoreInterceptor';
import { db } from '../../config-firebase/firebase.js';
import { getOneItemColleciton, logStockUsage } from '../../api/Api.js';
import { alertMinimunAmount, checkUnavaiableRawMaterial } from '../../Helpers/Helpers.js';

export const cleanObject = (obj) => {
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([_, value]) => value !== undefined && value !== null)
        .map(([key, value]) => [key, cleanObject(value)]), // Limpa recursivamente
    );
  }
  return obj; // Retorna o valor se não for objeto
};

function parseToNumber(value) {
  if (typeof value === 'string') {
    return Number(value.replace(',', '.'));
  }
  return Number(value);
}

function round(value, decimals = 2) {
  return Math.round(value * 10 ** decimals) / 10 ** decimals;
}

const hasWarningForProduct = (productName) => {
  const raw = localStorage.getItem('warningAmountMessage');
  let parsed = JSON.parse(raw);
  const res =
    Array.isArray(parsed) &&
    parsed.length > 0 &&
    parsed.some(
      (entry) => typeof entry === 'string' && entry.includes(productName),
    );
  return res;
};

const stockHistoryList = (
  item,
  account,
  paymentDate,
  pack,
  cost,
  unit,
  volume,
  previousVolume,
  previousCost,
  totalCost,
  totalVolume,
  orderNumber = '',
) => {
  const stockEventRegistration = {
    date: paymentDate,
    outputProduct: Number(volume).toFixed(2),
    category: account || 0,
    unit: unit,
    package: pack,
    inputProduct: 0,
    cost: cost,
    previousVolume: previousVolume,
    previousCost: previousCost,
    ContentsInStock: totalVolume,
    totalResourceInvested: Number(totalCost).toFixed(2),
    orderNumber: orderNumber,
  };
  return stockEventRegistration;
};

const updatingStockAndMenu = async (itemFinded, currentItem, { global, setLoadingAvailableMenuDishes } = {}) => {
  // Se o restaurante não opera com controle de estoque, não executa checagens nem dispara avisos
  if (!global?.hasRawMaterial) return;

  if (!itemFinded) {
    console.warn(`[STOCK] updatingStockAndMenu ignorado: "${currentItem.product}" não possui registro de estoque correspondente.`);
    return;
  }

  // Sempre atualiza a disponibilidade dos pratos no menu ao alterar o estoque
  if (setLoadingAvailableMenuDishes) setLoadingAvailableMenuDishes(true);
  const res = await checkUnavaiableRawMaterial(itemFinded.id);
  if (setLoadingAvailableMenuDishes) setLoadingAvailableMenuDishes(res);

  // Silencia avisos/alerta se os limites de aviso e indisponibilidade forem zero
  const isZeroThreshold = Number(itemFinded.minimumAmount) === 0 && Number(itemFinded.disabledDish) === 0;

  if (!isZeroThreshold && currentItem.totalVolume < itemFinded.minimumAmount) {
    if (!hasWarningForProduct(currentItem.product)) {
      console.log(`Aviso já registrado para ${currentItem.product}`);
      alert(
        `Volume do item ${currentItem.product} está abaixo do recomendado. Verifique o estoque.`,
      );

      const check = alertMinimunAmount(
        currentItem.product,
        currentItem.totalVolume,
        itemFinded.minimumAmount,
        currentItem.totalCost,
      );

      if (check && check.message) {
        try {
          const key = 'warningAmountMessage';
          let stored = localStorage.getItem(key);
          let warnings = stored ? JSON.parse(stored) : [];
          if (!Array.isArray(warnings)) warnings = [];
          warnings.push(check.message);
          localStorage.setItem(key, JSON.stringify(warnings));
          console.log('O que é esse global aqui  ', global);
          if (global?.setWarningLowRawMaterial) {
            global.setWarningLowRawMaterial((prev) => [...prev, check.message]);
          }
        } catch (err) {
          console.error(
            'Erro ao atualizar warningAmountMessage no localStorage',
            err,
          );
        }
      }
    }
  }
};

const handleStock = async (
  itemsStock,
  account = 'Editado',
  paymentDate = null,
  orderNumber = '',
  options = {},
) => {
  if (!Array.isArray(itemsStock)) {
    itemsStock = [itemsStock];
  }

  if (!paymentDate) {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0'); // Mês é zero-based
    const year = today.getFullYear();
    const hours = String(today.getHours()).padStart(2, '0');
    const minutes = String(today.getMinutes()).padStart(2, '0');
    const seconds = String(today.getSeconds()).padStart(2, '0');
    paymentDate = `${day}/${month}/${year} - ${hours}:${minutes}:${seconds}`;
  }

  for (let i = 0; i < itemsStock.length; i++) {
    let currentItem = itemsStock[i];

    // Busca o documento correspondente pelo nome do produto
    const stockCol = collection(db, 'stock');
    const q = query(stockCol, where('product', '==', currentItem.product));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const docSnap = querySnapshot.docs[0];
      const docRef = doc(db, 'stock', docSnap.id);

      try {
        const result = await runTransaction(db, async (transaction) => {
          const freshDoc = await transaction.get(docRef);
          if (!freshDoc.exists()) {
            throw new Error('Stock item not found during transaction');
          }
          const itemFinded = freshDoc.data();
          itemFinded.id = docSnap.id; // Garante o id

          // Executa os cálculos de volume e custo usando dados frescos do servidor
          const previousCost = parseToNumber(itemFinded.totalCost);
          const previousVolume = parseToNumber(itemFinded.totalVolume);
          const cost = account === 'Editado' ? 0 : currentItem.totalCost;
          const pack =
            account === 'Editado'
              ? Number(currentItem.amount)
              : Number(itemFinded.amount) + Number(currentItem.amount);
          const volume = account === 'Editado' ? 0 : currentItem.totalVolume;
          const unit = currentItem.unitOfMeasurement;

          let updatedTotalCost = previousCost;
          let updatedTotalVolume = previousVolume;

          if (
            account !== 'Editado'
          ) {
            const volumeBefore = parseToNumber(currentItem.totalVolume);
            updatedTotalVolume = round(volumeBefore + previousVolume, 4);

            if (updatedTotalVolume <= 0) {
              updatedTotalVolume = 0;
              updatedTotalCost = 0;
            } else if (previousVolume > 0) {
              // Redução proporcional do custo com base no volume restante
              updatedTotalCost = round(previousCost * (updatedTotalVolume / previousVolume), 2);
            } else {
              updatedTotalCost = 0;
            }
          }

          const updatedProduct = {
            ...itemFinded,
            totalCost: updatedTotalCost,
            totalVolume: updatedTotalVolume,
            amount: pack,
          };

          const logEvent = stockHistoryList(
            itemFinded,
            account,
            paymentDate,
            pack,
            cost,
            unit,
            volume,
            previousVolume,
            previousCost,
            updatedTotalCost,
            updatedTotalVolume,
            orderNumber,
          );

          const cleanedProduct = cleanObject(updatedProduct);
          delete cleanedProduct.UsageHistory;
          delete cleanedProduct.id;

          transaction.update(docRef, cleanedProduct);

          return { cleanedProduct, logEvent, itemFinded };
        });

        await logStockUsage(docSnap.id, result.logEvent);
        await updatingStockAndMenu(result.itemFinded, result.cleanedProduct, options);

      } catch (txError) {
        console.error(`Erro na transação de estoque para ${currentItem.product}:`, txError);
      }

    } else {
      const logEvent = stockHistoryList(
        currentItem,
        account,
        paymentDate,
        0,
        currentItem.totalCost,
        currentItem.totalVolume,
        orderNumber,
      );
      let cleanedItem = cleanObject(currentItem);
      delete cleanedItem.UsageHistory;
      
      const newDocRef = await addDoc(collection(db, 'stock'), cleanedItem);
      await logStockUsage(newDocRef.id, logEvent);
      await updatingStockAndMenu(null, cleanedItem, options);
    }
  }
};

const updateSideDihesInStock = async (request, dateTime, orderNumber = '', options = {}) => {
  if (!request || !Array.isArray(request) || request.length === 0) return;

  for (let i = 0; i < request.length; i++) {
    const currentItem = request[i];
    const account = currentItem.name;
    if (
      currentItem.sideDishes &&
      Array.isArray(currentItem.sideDishes) &&
      currentItem.sideDishes.length > 0
    ) {
      for (let j = 0; j < currentItem.sideDishes.length; j++) {
        const sideDish = currentItem.sideDishes[j];
        
        if (sideDish.isBasic) continue; // Pula baixa de estoque se for modo básico (sem vínculo)
        
        const portionVal = sideDish.portionUsed !== undefined ? sideDish.portionUsed : sideDish.portionCost;
        
        const currentObj = {
          product: sideDish.name,
          totalVolume: -parseToNumber(portionVal),
          unitOfMeasurement: sideDish.unit || '',
          CostPerUnit: sideDish.portionCost || 0,
          amount: 0,
          totalCost: 0,
          volumePerUnit: 0,
        };
        await handleStock(currentObj, account, dateTime, orderNumber, options);
      }
    }
  }
};

export const updateIngredientsStock = async (item, options = {}) => {
  const dateTime = item.dateTime;
  const { request } = item;
  const orderNumber = item.countRequest || '';

  if (request && request.length > 0) {
    await updateSideDihesInStock(request, dateTime, orderNumber, options);
  }

  for (let i = 0; i < request.length; i++) {
    const currentItem = request[i];

    const account = currentItem.name;
    const FinalingridientsList = currentItem?.recipe?.FinalingridientsList;
    
    console.log(`[STOCK DEBUG] Processing item: ${account}, id: ${currentItem.id}, size: ${currentItem.size}`);
    console.log(`[STOCK DEBUG] FinalingridientsList:`, FinalingridientsList);

    if (!FinalingridientsList) {
      console.warn(`[STOCK DEBUG] No FinalingridientsList for ${account}. recipe object:`, currentItem?.recipe);
      alert(
        'Este produto precisa ter uma receita cadastrada para ser vendido.',
      );
      continue; // ou continue, dependendo do seu fluxo
    }

    const size = currentItem?.size;
    let listBySize = null;

    if (Array.isArray(FinalingridientsList)) {
      listBySize = FinalingridientsList;
      console.log(`[STOCK DEBUG] FinalingridientsList is array. listBySize length: ${listBySize.length}`);
    } else if (typeof FinalingridientsList === 'object') {
      console.log(`[STOCK DEBUG] FinalingridientsList is object.`);
      if (size) {
        if (Array.isArray(FinalingridientsList[size])) {
          listBySize = FinalingridientsList[size];
          console.log(`[STOCK DEBUG] Found list for exact size: ${size}. Length: ${listBySize.length}`);
        } else {
          console.log(`[STOCK DEBUG] Exact size not found. Fetching dish definition for mapping...`);
          try {
            const dishDef = await getOneItemColleciton('item', currentItem.id);
            console.log(`[STOCK DEBUG] dishDef CustomizedPrice:`, dishDef?.CustomizedPrice);
            if (dishDef && dishDef.CustomizedPrice) {
              const mapping = {
                [dishDef.CustomizedPrice.firstLabel]: 'firstPrice',
                [dishDef.CustomizedPrice.secondLabel]: 'secondPrice',
                [dishDef.CustomizedPrice.thirdLabel]: 'thirdPrice',
              };
              const mappedKey = mapping[size];
              console.log(`[STOCK DEBUG] Mapped key for size ${size} is ${mappedKey}`);
              if (mappedKey && Array.isArray(FinalingridientsList[mappedKey])) {
                listBySize = FinalingridientsList[mappedKey];
                console.log(`[STOCK DEBUG] Found list using mapped key ${mappedKey}. Length: ${listBySize.length}`);
              }
            }
          } catch (err) {
            console.error(`Erro ao buscar definição do prato ${currentItem.name}:`, err);
          }

          // Fallback difuso caso as labels difiram por espaços ou caixa (ex: "330 ml" vs "330ml")
          if (!listBySize) {
            console.log(`[STOCK DEBUG] Fallback difuso para tamanho: ${size}`);
            const normalizedSize = size.replace(/\s+/g, '').toLowerCase();
            const foundKey = Object.keys(FinalingridientsList).find(k => 
              k.replace(/\s+/g, '').toLowerCase() === normalizedSize
            );
            console.log(`[STOCK DEBUG] Fallback difuso encontrou chave: ${foundKey}`);
            if (foundKey && Array.isArray(FinalingridientsList[foundKey])) {
              listBySize = FinalingridientsList[foundKey];
              console.log(`[STOCK DEBUG] Found list using fallback key ${foundKey}. Length: ${listBySize.length}`);
            }
          }
        }
      } else {
        console.log(`[STOCK DEBUG] Item has no size specified, but recipe is object. Cannot determine ingredients list.`);
      }
    }

    if (Array.isArray(listBySize)) {
      console.log(`[STOCK DEBUG] Proceeding to deduct ${listBySize.length} ingredients for ${account}`);
      for (let j = 0; j < listBySize.length; j++) {
        const ingredient = listBySize[j];
        const currentObj = {
          product: ingredient.name,
          totalVolume: -Number(ingredient.amount.replace(',', '.')),
          unitOfMeasurement: ingredient.unitOfMeasurement || '',
          CostPerUnit: ingredient.portionCost || 0,
          amount: 0,
          totalCost: 0,
          volumePerUnit: 0,
        };
        console.log(`[STOCK DEBUG] Deducting ingredient: ${ingredient.name}, volume: ${currentObj.totalVolume}`);
        await handleStock(currentObj, account, dateTime, orderNumber, options);
      }
      console.log(`[STOCK DEBUG] Finished deducting ingredients for ${account}`);
    } else {
      console.error(`[STOCK DEBUG] CRITICAL: Não foi possível encontrar a lista de ingredientes para o tamanho "${size}" do prato "${currentItem.name}"! recipe object:`, currentItem?.recipe);
    }
  }
};

